import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ClientCancelAppointmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentName: string;
  scheduledDate?: Date;
  onConfirmCancel: (reason: string) => void;
}

const ClientCancelAppointmentDialog: React.FC<ClientCancelAppointmentDialogProps> = ({
  isOpen,
  onClose,
  appointmentName,
  scheduledDate,
  onConfirmCancel,
}) => {
  const [t] = useTranslation('calendar');
  const [cancelReason, setCancelReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtherReason, setIsOtherReason] = useState(false);
  const [customReason, setCustomReason] = useState('');

  // Motivos específicos para clientes
  const predefinedReasons = [
    { key: 'emergency', value: t('emergency') || 'Emergencia personal' },
    { key: 'illness', value: t('illness') || 'Enfermedad' },
    { key: 'work_conflict', value: t('work_conflict') || 'Conflicto laboral' },
    { key: 'travel_issues', value: t('travel_issues') || 'Problemas de transporte' },
    { key: 'family_commitment', value: t('family_commitment') || 'Compromiso familiar' },
    { key: 'schedule_change', value: t('schedule_change') || 'Cambio de planes' },
    { key: 'other', value: t('other_reason') || 'Otro motivo' },
  ];

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const handleConfirm = async () => {
    let finalReason = '';
    
    if (isOtherReason) {
      finalReason = customReason;
    } else {
      // Find the translated value for the selected reason key
      const selectedReason = predefinedReasons.find(reason => reason.key === cancelReason);
      finalReason = selectedReason ? selectedReason.value : cancelReason;
    }
    
    if (!finalReason.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirmCancel(finalReason);
      handleClose();
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCancelReason('');
    setCustomReason('');
    setIsOtherReason(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleReasonChange = (reason: string) => {
    setCancelReason(reason);
    setIsOtherReason(reason === 'other');
    if (reason !== 'other') {
      setCustomReason('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md">
        <h3 className="font-bold text-lg text-brand-heading mb-4">
          {t('cancel_appointment') || 'Cancelar Cita'}
        </h3>

        <div className="space-y-4">
          {/* Appointment Details */}
          <div className="bg-base-200 p-4 rounded-lg">
            <h4 className="font-semibold text-brand-body mb-2">
              {t('appointment_details') || 'Detalles de la cita'}
            </h4>
            <div className="space-y-1 text-sm text-brand-caption">
              <p>
                <span className="font-medium">{t('name') || 'Nombre'}:</span> {appointmentName}
              </p>
              {scheduledDate && (
                <p>
                  <span className="font-medium">{t('date_label') || 'Fecha'}:</span> {formatDate(scheduledDate)}
                </p>
              )}
            </div>
          </div>

          {/* Cancellation Reason */}
          <div>
            <p className="text-brand-body font-semibold mb-3">
              {t('select_cancellation_reason') || 'Selecciona el motivo de cancelación'}
            </p>
            <div className="space-y-2">
              {predefinedReasons.map((reason) => (
                <label key={reason.key} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason.key}
                    checked={cancelReason === reason.key}
                    onChange={(e) => handleReasonChange(e.target.value)}
                    className="radio radio-primary radio-sm"
                  />
                  <span className="text-brand-body text-sm">{reason.value}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Custom reason input */}
          {isOtherReason && (
            <div>
              <label className="text-brand-body font-semibold mb-2 block">
                {t('additional_details') || 'Detalles adicionales'}
              </label>
              <textarea
                className="textarea textarea-bordered w-full border-brand-primary focus:border-brand-secondary focus:outline-none focus:ring-2 focus:ring-brand-secondary/20"
                placeholder={t('enter_additional_details') || 'Describe el motivo de la cancelación...'}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {/* Warning */}
          <div className="alert alert-warning">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-sm">
              {t('client_cancellation_warning') || 'Esta acción no se puede deshacer. Tu cita será cancelada definitivamente.'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-action">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('cancel_button') || 'Cancelar'}
          </button>
          <button
            type="button"
            className="btn btn-error"
            onClick={handleConfirm}
            disabled={!cancelReason || (isOtherReason && !customReason.trim()) || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                {t('canceling') || 'Cancelando...'}
              </>
            ) : (
              t('confirm_cancellation') || 'Confirmar cancelación'
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default ClientCancelAppointmentDialog;
